import { Fragment, useMemo } from 'react';

import {
  FilterAppliedProps,
  FilterComparer,
  FilterConfig,
  FilterMenuProps,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconApplicants } from '@components/icons/applicants';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconIntegration } from '@components/icons/integration';

import { IntegrationIcon, IntegrationKey } from 'components/IntegrationIcon';
import { useStore } from 'models/Provider';
import { ApplicationTriggerStatus } from 'types/applications';

import { FilterActionButtons } from './filter-action-buttons';
import { FilterComparerMenu } from './filter-comparer-menu';

const INTEGRATION_STATUS_COMPARERS: Array<FilterComparer> = ['is', 'not_is'];

function FilterApplicationIntegrationStatusApplied({ data }: FilterAppliedProps) {
  const { t } = useStore();

  return (
    <Filters.AppliedFilter
      icon={<IconApplicants />}
      category={`${t('applicants.filters.integration.title')}: ${data.field}`}
      value={t(`applicants.filters.integrations.${data.value}`)}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

type ExtraProps = {
  integrations: Array<{
    label: IntegrationKey;
    value: string;
    statuses: Array<ApplicationTriggerStatus>;
  }>;
};

function FilterApplicationIntegrationStatusMenu({ integrations }: FilterMenuProps<ExtraProps>) {
  const { t } = useStore();
  const { state, actions, hasValue } = useCurrentFilter();

  const selectedIntegration = integrations.find((integration) => integration.label === state.field);

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.integration.title')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />
      {state.field && (
        <Filters.ListItem
          heading
          label={state.field}
          icon={<IconArrowLeft />}
          onClick={actions.unselectField}
        />
      )}

      <Filters.ListSeparator />

      <Fragment>
        {!state.field ? (
          integrations.map((integration) => (
            <Filters.ListItem
              key={integration.label}
              label={integration.label}
              icon={<IntegrationIcon size="1.5rem" integrationName={integration.label} />}
              onClick={() => actions.selectField(integration.label)}
            />
          ))
        ) : !state.comparer ? (
          <FilterComparerMenu
            comparers={INTEGRATION_STATUS_COMPARERS}
            onComparerSelect={actions.selectComparer}
          />
        ) : (
          selectedIntegration && (
            <Filters.FilterValueMenu
              items={selectedIntegration.statuses.map((status) => ({
                label: t(`applicants.filters.integrations.${status}`),
                value: status,
              }))}
              isMulti={false}
              onSetValue={actions.selectValue}
              state={state}
            />
          )
        )}
      </Fragment>
      {state.value && <FilterActionButtons disableApply={!hasValue} />}
    </Filters.List>
  );
}

export function useFilterApplicationIntegrationStatus(): FilterConfig | null {
  const { t, current } = useStore();
  // @ts-ignore-next-line current info is not typescript
  const integrations = current.info.isHiredTriggers.map((trigger) => ({
    value: trigger.name,
    label: trigger.name,
    statuses: trigger.statuses,
  }));

  return useMemo(() => {
    if (integrations.length === 0) return null;

    return {
      category: 'triggers',
      label: t('applicants.filters.integration.title'),
      icon: <IconIntegration />,
      AppliedFilter: FilterApplicationIntegrationStatusApplied,
      Menu: FilterApplicationIntegrationStatusMenu,
      extraProps: {
        integrations,
      },
      toQueryData: (data) => {
        switch (data.comparer) {
          case 'is':
            return { [data.category]: `${data.field}__${data.value}` };
          case 'not_is':
            return { [`${data.category}__exclude`]: `${data.field}__${data.value}` };
          default:
            return {};
        }
      },
    };
  }, [integrations, t]);
}
